import { Badge, Skeleton, tableHelpers, Tooltip } from '@round/creatorbase-ui';
import { asMoneyWithoutZeros, buildInstagramUserUrl, buildTiktokUserUrl } from 'helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { getMeta, CellContext, ExplorePlatform, EXPLORE_PLATFORMS } from '../../helpers';
import { formatNumber, formatPercentage } from '../../../../utils/creatorSearchUtils';
import styles from './PlatformSelectorCell.module.css';
import cn from 'classnames';
import { platformLabels, platformIcons, platformSolidIcons } from 'Modules/Plans/helpers';
import { Fragment } from 'react';

function PlatformIcon({
    platform,
    isSolid,
    isConnected,
}: {
    platform: ExplorePlatform;
    isSolid?: boolean;
    isConnected: boolean;
}) {
    const ColorIcon = platformIcons[platform];
    const SolidIcon = platformSolidIcons[platform];
    const Icon = isSolid ? SolidIcon : ColorIcon;

    return <Icon className={cn(styles.platformIcon, { [styles.faded]: !isConnected })} aria-hidden />;
}

export const PlatformSelectorCell = ({ row: { original }, table }: CellContext) => {
    const { isLoading, getRowSelectedPlatform, onRowPlatformSelect } = getMeta(table);
    const isBaseLoading = tableHelpers.isLoadingRow(original) || isLoading;
    const { getCurrencyById } = useCurrencies();

    if (isBaseLoading) {
        return (
            <div className={styles.container}>
                <Skeleton className={styles.pillSkeleton} />
                <Skeleton className={styles.iconSkeleton} />
            </div>
        );
    }

    const selectedPlatform = getRowSelectedPlatform(original);
    const selectedAccount = original.platforms?.[selectedPlatform]?.[0] || undefined;

    const profileUrl = selectedAccount?.platform_username
        ? selectedPlatform === 'tiktok'
            ? buildTiktokUserUrl(selectedAccount.platform_username)
            : buildInstagramUserUrl(selectedAccount.platform_username)
        : null;

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            {EXPLORE_PLATFORMS.map((platform) => {
                const platformLabel = platformLabels[platform];

                if (platform === selectedPlatform) {
                    return (
                        <Fragment key={platform}>
                            {profileUrl ? (
                                <Badge asChild className={cn(styles.pill, styles[selectedPlatform])}>
                                    <a
                                        href={profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <PlatformIcon platform={selectedPlatform} isSolid isConnected />
                                        {platformLabel}
                                    </a>
                                </Badge>
                            ) : (
                                <Badge className={cn(styles.pill, styles[selectedPlatform])}>
                                    <PlatformIcon platform={selectedPlatform} isSolid isConnected />
                                    {platformLabel}
                                </Badge>
                            )}
                        </Fragment>
                    );
                }

                const isConnected = (original.platforms?.[platform]?.length ?? 0) > 0;
                const account = original.platforms?.[platform]?.[0];
                const metrics = original.metrics?.[platform];

                return (
                    <Tooltip key={platform}>
                        <Tooltip.Trigger asChild>
                            <button
                                className={styles.platformIconButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRowPlatformSelect(original._id, platform);
                                }}
                                disabled={!isConnected}
                            >
                                <PlatformIcon platform={platform} isConnected={isConnected} />
                            </button>
                        </Tooltip.Trigger>

                        <Tooltip.Content side="bottom">
                            {isConnected ? (
                                <div className={styles.tooltipStats}>
                                    <p className={styles.tooltipPlatformLabel}>{platformLabel}</p>
                                    <p className={styles.tooltipStatRow}>
                                        <span className={styles.tooltipStatLabel}>Followers</span>
                                        <span>{formatNumber(metrics?.total_follower_count)}</span>
                                    </p>
                                    <p className={styles.tooltipStatRow}>
                                        <span className={styles.tooltipStatLabel}>Cost</span>
                                        <span>
                                            {asMoneyWithoutZeros(
                                                account?.cost,
                                                getCurrencyById?.(account?.currency_id)
                                            )}
                                        </span>
                                    </p>
                                    <p className={styles.tooltipStatRow}>
                                        <span className={styles.tooltipStatLabel}>Avg Plays</span>
                                        <span>{formatNumber(metrics?.avg_video_views_per_post)}</span>
                                    </p>
                                    <p className={styles.tooltipStatRow}>
                                        <span className={styles.tooltipStatLabel}>CPM</span>
                                        <span>
                                            {asMoneyWithoutZeros(
                                                account?.estimated_cpm,
                                                getCurrencyById?.(account?.currency_id)
                                            )}
                                        </span>
                                    </p>
                                    <p className={styles.tooltipStatRow}>
                                        <span className={styles.tooltipStatLabel}>ER%</span>
                                        <span>{formatPercentage(metrics?.avg_engagement_rate)}</span>
                                    </p>
                                </div>
                            ) : (
                                `${platformLabel} not connected`
                            )}
                        </Tooltip.Content>
                    </Tooltip>
                );
            })}
        </div>
    );
};
